/**
 * IR E VIR ENTRE A TELA E O DESENHO — as camadas interativas convertem aqui, e só aqui.
 *
 * Todo componente que põe algo interativo por cima de uma `Scene` enfrenta o mesmo
 * problema: as formas da cena moram dentro de um `<g>` que inverte o eixo Y
 * (o `flip` de `render/svg.ts`), porque no modelo o y cresce para cima e no SVG cresce para
 * baixo. A camada interativa precisa decidir de que lado dessa inversão ela fica — e
 * decidir errado não quebra nada visivelmente: só desloca o alvo do clique.
 *
 * FOI ASSIM QUE OS ALVOS DA BALANÇA SAÍRAM ESPELHADOS. A camada era anexada à raiz do
 * SVG, fora do grupo invertido, enquanto as peças estavam dentro dele. Os blocos
 * apareciam no lugar certo e o clique caía refletido — sem erro no console, sem teste
 * vermelho, e visível só para quem tentasse usar.
 *
 * AS DUAS ESTRATÉGIAS, E QUANDO USAR CADA UMA:
 *
 *  1. **Ficar fora do grupo invertido e converter o y na mão** (`yTela`). É o que faz
 *     quem desenha TEXTO na camada: dentro do grupo, o rótulo sairia de cabeça para
 *     baixo. Arrastador, régua e regiões fazem assim, e por isso.
 *  2. **Entrar no mesmo grupo das formas** e não converter nada. É o que faz quem só
 *     desenha alvos invisíveis e formas simétricas, como a balança. Mais simples, e
 *     sem a chance de esquecer uma conversão — mas proibido para texto.
 *
 * Misturar as duas é o defeito: a balança desenhava pelo caminho 1 e mirava pelo 2.
 *
 * As três cópias de `yTela` e as duas de `paraModelo` que existiam eram idênticas ao
 * caractere. A armadilha já mordeu duas vezes numa semana; agora há um lugar só onde
 * ela pode morder, e ele tem teste. A constante da reflexão, essa, mora em
 * `render/scene.ts` (`refletirY`), porque o renderizador também precisa dela — e o
 * renderizador não pode depender da interface.
 */

import type { Vec2 } from '../geom/vec.ts';
import { refletirY, type Rect } from '../render/scene.ts';

export interface Conversor {
  /**
   * Y do modelo → y do SVG, para quem desenha FORA do grupo invertido.
   *
   * A conta é a reflexão em torno do meio do viewBox: o topo vira a base e a base vira
   * o topo, sem mexer na escala.
   */
  yTela(y: number): number;
  /**
   * Ponteiro → coordenadas do desenho, já com o Y do modelo.
   *
   * Respeita o `meet` do `preserveAspectRatio`: o SVG centraliza o desenho na caixa e
   * sobra margem de um dos lados, e é essa margem que a conta desconta. Sem ela, o
   * clique erra cada vez mais à medida que a janela se afasta da proporção do viewBox
   * — o erro some quando a proporção bate, que é justamente a tela de quem testa.
   */
  paraModelo(e: { readonly clientX: number; readonly clientY: number }): Vec2;
}

/**
 * A conta, num lugar só: pixel da tela → ponto do modelo, com a escala de volta.
 *
 * Recebe a caixa medida e o viewBox em vez de ir buscá-los, porque nem todo chamador
 * tem os dois parados: a prancheta de construção dá zoom, e o viewBox dela muda a cada
 * gesto. Era esse detalhe que fazia a cópia de lá parecer diferente das outras duas —
 * a fórmula sempre foi a mesma.
 *
 * `escala` é quantas unidades do modelo cabem num pixel, e serve a quem precisa de uma
 * tolerância que não mude com o zoom (o ímã da prancheta, por exemplo).
 */
export function pontoNoModelo(
  caixa: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
  },
  vb: Rect,
  cx: number,
  cy: number,
): { p: Vec2; escala: number } {
  const s = Math.min(caixa.width / vb.w, caixa.height / vb.h) || 1;
  const ox = (caixa.width - vb.w * s) / 2;
  const oy = (caixa.height - vb.h * s) / 2;
  const x = vb.x + (cx - caixa.left - ox) / s;
  const yt = vb.y + (cy - caixa.top - oy) / s;
  return { p: { x, y: refletirY(vb, yt) }, escala: 1 / s };
}

export function conversor(svg: SVGSVGElement, vb: Rect): Conversor {
  return {
    yTela: (y) => refletirY(vb, y),
    paraModelo: (e) => pontoNoModelo(svg.getBoundingClientRect(), vb, e.clientX, e.clientY).p,
  };
}

/**
 * O grupo que carrega a inversão do eixo Y, para quem escolhe a estratégia 2.
 *
 * Em vez de procurar o `<g>` por posição — que amarra o componente à estrutura interna
 * do renderizador —, pergunta-se a uma forma já desenhada quem é o pai dela. Enquanto
 * as formas da cena estiverem juntas, a resposta está certa por construção.
 */
export function grupoDaCena(svg: SVGSVGElement, umaForma: Element | null | undefined): Element {
  return umaForma?.parentNode instanceof Element ? umaForma.parentNode : svg;
}
